import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { HashLink } from 'react-router-hash-link';
import { PromisePool } from 'es6-promise-pool';
import Select from 'react-select';
import { connect } from 'react-redux';
import {
  Button,
  Col,
  Container,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  APIgetUsers,
  APIgetUserStats,
  APIdeactivateUser,
} from '../../../api/User';

import AccessModalSelectItem from '../../Access/ModalSelectItem';
import ProcessingProgress from '../../ProcessingProgress';
import ProcessingSpinner from '../../ProcessingSpinner';

import { addFlashMessage } from '../../../helpers/FlashMessages';

import ErrorBoundary from '../../ErrorBoundary';
import Loading from '../../Loading';

class UserDeactivateModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      users: [],
      usersStats: [],
      view: null,
      loading: true,
      usersLoading: false,
      deactivating: false,
      updatedUsers: 0,
      targetUser: null,
      assignAssets: false,
      isAnyUserWorkspaceOwner: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleChangeSelectedUser = this.handleChangeSelectedUser.bind(this);
    this.handleDeactivate = this.handleDeactivate.bind(this);
    this.handleLinkClose = this.handleLinkClose.bind(this);
    this.resetView = this.resetView.bind(this);
  }

  componentDidMount() {
    this.loadUserStats();
    this.loadUsers();
    this.handleIsAnyUserWorkspaceOwner();
  }

  getOptions() {
    const { users: selectedUsers } = this.props;
    const { users } = this.state;
    const selectedUsersUuids = selectedUsers.map(u => (u.uuid));

    const options = [];
    users
      .filter(u => (!selectedUsersUuids.includes(u.uuid)))
      .filter(u => (!u.deactivated_at))
      .forEach((i) => {
        options.push({
          uuid: i.uuid,
          name: i.name,
          email: i.email,
          avatar: i.avatar_url,
          type: 'user',
        });
      });

    return options;
  }

  getUserStats(userUuid) {
    const { intl } = this.props;
    const { usersStats } = this.state;
    const stats = usersStats[userUuid];
    if (stats) {
      let filesText = '';
      if (stats.files) {
        filesText = intl.formatMessage({
          defaultMessage: '{files} {files, plural, one {file} other {files}}',
        }, { files: stats.files });
      } else {
        filesText = intl.formatMessage({ defaultMessage: 'no files' });
      }

      let collectionText = '';
      if (stats.collections) {
        collectionText = `, ${intl.formatMessage({
          defaultMessage: '{collections} {collections, plural, one {Collection} other {Collections}}',
        }, { collections: stats.collections })}`;
      } else {
        collectionText = `, ${intl.formatMessage({ defaultMessage: 'no Collections' })}`;
      }

      return ` ${filesText}${collectionText}`;
    }
    return null;
  }

  getUsersPromisePool() {
    const { users } = this.props;
    const { targetUser } = this.state;

    let counterUsers = 0;
    const promiseProducerUsers = () => {
      if (counterUsers < users.length) {
        const user = users[counterUsers];
        counterUsers += 1;
        return APIdeactivateUser(user.uuid, targetUser ? targetUser.uuid : null).then(() => {
          this.setState({ updatedUsers: counterUsers });
        });
      }
      return null;
    };

    return new PromisePool(promiseProducerUsers, 2);
  }

  getUsersStatsPromisePool() {
    const { users } = this.props;

    let counterUsers = 0;
    const promiseProducerUsersStats = () => {
      if (counterUsers < users.length) {
        const user = users[counterUsers];
        counterUsers += 1;
        return APIgetUserStats(user.uuid).then((response) => {
          if (response.data && response.data.uuid) {
            const { usersStats } = this.state;
            usersStats[response.data.uuid] = response.data;
            this.setState({ usersStats });
          }
        });
      }
      return null;
    };

    return new PromisePool(promiseProducerUsersStats, 2);
  }

  loadUserStats() {
    this.setState({ loading: true });
    this.getUsersStatsPromisePool().start().then(() => {
      this.setState({ loading: false });
    });
  }

  loadUsers() {
    this.setState({ usersLoading: true });

    APIgetUsers().then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ users: data });
      }
      this.setState({ usersLoading: false });
    });
  }

  handleDeactivate() {
    const {
      intl,
      users,
      handleClose,
      afterDeactivateUsers,
    } = this.props;

    this.setState({ deactivating: true });

    this.getUsersPromisePool().start().then(() => {
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage(
          {
            defaultMessage: '{count, plural, one {User has} other {Users have}} been deactivated',
          },
          { count: users.length },
        ),
      });
      afterDeactivateUsers();
      handleClose();
    }).catch(() => {
      this.setState({
        view: 'error',
        deactivating: false,
      });
    });
  }

  handleChange(e) {
    const { target } = e;
    const { name } = target;

    switch (name) {
      case 'assign-assets':
        this.setState({ assignAssets: target.checked });
        if (!target.checked) {
          this.setState({ targetUser: null });
        }
        break;
      default:
    }
  }

  handleChangeSelectedUser(targetUser) {
    this.setState({ targetUser });
  }

  handleIsAnyUserWorkspaceOwner() {
    const { users, loggedInUser } = this.props;
    const isAnyUserWorkspaceOwner =
      users.find(u => u.uuid === loggedInUser.client.workspace_owner.uuid);

    if (isAnyUserWorkspaceOwner) {
      this.setState({ isAnyUserWorkspaceOwner: true });
    }
  }

  resetView(e) {
    e.preventDefault();
    this.setState({ view: null });
  }

  usersHaveAssets() {
    const { usersStats } = this.state;
    let usersHaveAssets = false;

    Object.keys(usersStats).forEach((s) => {
      if (usersStats[s].files || usersStats[s].collections) {
        usersHaveAssets = true;
      }
    });

    return usersHaveAssets;
  }

  handleLinkClose() {
    const { handleClose } = this.props;

    setTimeout(() => {
      handleClose();
    }, 100);
  }

  render() {
    const {
      view,
      targetUser,
      loading,
      usersLoading,
      deactivating,
      assignAssets,
      updatedUsers,
      isAnyUserWorkspaceOwner,
    } = this.state;
    const {
      intl,
      users,
      handleClose,
      loggedInUser,
    } = this.props;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {intl.formatMessage({
            defaultMessage: 'Deactivate {count, plural, one {user} other {users}}',
          }, { count: users.length })}
        </ModalHeader>
        {!view && (
          <ModalBody style={{ overflow: 'scroll' }}>
            {loading ? (
              <Loading />
            ) : (
              <>
                <p>
                  {intl.formatMessage({
                    defaultMessage: 'You are about to deactivate the following {count, plural, one {user} other {users}}:',
                  }, { count: users.length })}
                </p>
                <ul>
                  {users.map(u => (
                    <li key={u.uuid}>
                      <strong>{u.name}</strong>
                      {loggedInUser.client.workspace_owner.uuid === u.uuid && (
                        <>
                          {' '}
                          <i id="modal-user-workspace-owner" className="fa-regular fa-crown" />
                          <UncontrolledTooltip
                            target="modal-user-workspace-owner"
                            boundariesElement="viewport"
                            fade={false}
                          >
                            {intl.formatMessage({ defaultMessage: 'Workspace owner' })}
                          </UncontrolledTooltip>
                        </>
                      )}
                      {this.getUserStats(u.uuid)}
                    </li>
                  ))}
                </ul>
                {isAnyUserWorkspaceOwner && (
                  <div className="error center">
                    {intl.formatMessage({
                      defaultMessage: 'You cannot deactivate a workspace owner.{br}Change your workspace owner',
                    }, { br: <br /> })}{' '}
                    <HashLink to="/admin/settings/owner-details" onClick={this.handleLinkClose}>
                      {intl.formatMessage({ defaultMessage: 'here' })}
                    </HashLink>.
                  </div>
                )}
                {this.usersHaveAssets() && (
                  <>
                    <hr />
                    <div className="user-selector-label">
                      <Label>
                        <Input
                          type="checkbox"
                          name="assign-assets"
                          className="input-check check-s form-check-input no-margin"
                          value={true}
                          checked={assignAssets}
                          onChange={this.handleChange}
                          disabled={isAnyUserWorkspaceOwner}
                        />
                        {' '}
                        {intl.formatMessage({ defaultMessage: 'Assign assets to another user?' })}
                      </Label>
                    </div>
                  </>
                )}
                <div className="user-selector">
                  {assignAssets && (
                    <Select
                      id="new-user-selector"
                      inputId="new-user-selector-input"
                      name="new-user"
                      className="user-select"
                      classNamePrefix="user-select"
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Select user to assign files and Collections to',
                      })}
                      noOptionsMessage={() => (intl.formatMessage({ defaultMessage: 'No users' }))}
                      getOptionValue={option => option.uuid}
                      getOptionLabel={option => option.name}
                      components={{ Option: AccessModalSelectItem }}
                      isDisabled={usersLoading}
                      isLoading={usersLoading}
                      options={this.getOptions()}
                      onChange={this.handleChangeSelectedUser}
                      value={targetUser}
                    />
                  )}
                </div>
              </>
            )}
          </ModalBody>
        )}

        {(view === 'error' && !deactivating) && (
          <ModalBody className="modal-msg-body modal-msg-error">
            <Container>
              <Row>
                <Col>
                  {intl.formatMessage({ defaultMessage: 'Something went wrong. Please try again or contact us.' })}
                </Col>
              </Row>
            </Container>
          </ModalBody>
        )}

        {(!view && !loading) && (
          <ModalFooter className="justify-r">
            <div className="buttons-group">
              <Button
                color="secondary"
                onClick={handleClose}
                disabled={deactivating}
              >
                {intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
              <Button
                color="primary"
                onClick={this.handleDeactivate}
                disabled={deactivating || isAnyUserWorkspaceOwner}
              >
                <ProcessingSpinner show={deactivating} />
                {intl.formatMessage({ defaultMessage: 'Deactivate' })}
                {users.length > 1 && (
                  <ProcessingProgress
                    show={deactivating}
                    progress={{ total: users.length, done: updatedUsers }}
                  />
                )}
              </Button>
            </div>
          </ModalFooter>
        )}

        {(view === 'error' && !deactivating) && (
          <ModalFooter className="justify-l">
            <div className="buttons-group">
              <Button color="link" onClick={this.resetView}>
                <i className="far fa-long-arrow-left" />
                {intl.formatMessage({ defaultMessage: 'Back' })}
              </Button>
            </div>
          </ModalFooter>
        )}
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  loggedInUser: state.Auth.profile,
});

UserDeactivateModal.propTypes = {
  intl: PropTypes.object.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  afterDeactivateUsers: PropTypes.func,
};

UserDeactivateModal.defaultProps = {
  afterDeactivateUsers: () => {},
};

export default connect(mapStateToProps)(injectIntl(UserDeactivateModal));
